import React, { Fragment, useMemo } from "react";
import { useQuery } from "urql";
import { keyBy } from "lodash";
import { Link } from "wouter";

import { userHaveAccessTo } from "../../common/Permissions";
import { Me, PermissionAssetEnum, Role, User, UserStatusEnum } from "../../types.generated";
import { ADMIN_GET_ME, ADMIN_GET_USERS } from "../../../../graphql/src/user/queries";
import { formikUrqlErrorFormater } from "../../../../components/src/common/formik-urql-error-helper";
import {
    SelectColumnFilter,
    SelectColumnFilterArrayOfIds,
    SelectColumnFilterType
} from "../../../../components/src/react-table/ReactTableFilters";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";

import type { CombinedError } from "urql";

function AdminUsersPageError(props: { title: string; error: CombinedError }): React.ReactElement {
    const messages =
        props.error.graphQLErrors && props.error.graphQLErrors.length > 0
            ? props.error.graphQLErrors.map((e) => e.message)
            : [formikUrqlErrorFormater(props.error)];
    return (
        <div className="m-4 p-4" role="alert" style={{ border: "1px solid #c00", backgroundColor: "#fff5f5", borderRadius: 4 }}>
            <h2 style={{ marginTop: 0, color: "#900" }}>{props.title}</h2>
            {messages.map((msg, i) => (
                <p key={i} style={{ color: "#700", whiteSpace: "pre-wrap" }}>
                    {msg}
                </p>
            ))}
        </div>
    );
}

export function AdminUsersPage(): React.ReactElement {
    const [{ fetching: loadingMe, error: errorMe, data: getMeData }] = useQuery<{ me?: Partial<Me> }>({ query: ADMIN_GET_ME });
    const [{ fetching: loading, error, data }] = useQuery<{ users?: Partial<User>[]; roles?: Partial<Role>[] }>({
        query: ADMIN_GET_USERS,
        requestPolicy: "network-only"
    });

    const columns = useMemo(() => {
        let rolesById: Record<string, { _id: string; name: string }> = {};
        const clientsById: Record<string, { _id: string; name: string }> = {};
        const usedRolesById: Record<string, { _id: string; name: string }> = {};

        if (data?.roles && Array.isArray(data.roles)) {
            rolesById = keyBy(data.roles, "_id") as Record<string, { _id: string; name: string }>;
        }

        if (data?.users && Array.isArray(data.users)) {
            data.users.forEach((user) => {
                if (user.clients && Array.isArray(user.clients)) {
                    user.clients.forEach((client) => {
                        clientsById[client._id] = client;
                    });
                }
                if (user.roleIds && Array.isArray(user.roleIds)) {
                    user.roleIds.forEach((id) => {
                        const role = rolesById[id];
                        if (role) {
                            usedRolesById[id] = role;
                        }
                    });
                }
            });
        }
        return [
            {
                header: "Id",
                accessorKey: "_id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/admin/users/${row.original._id}`}>{cellProps.getValue()}</Link>;
                }
            },
            {
                header: "Status",
                accessorKey: "status",
                filter: SelectColumnFilterType(UserStatusEnum)
            },
            {
                header: "Clients",
                accessorKey: "clientIds",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (value) {
                        return (
                            <div className="flex flex-col">
                                {value.map((id) => {
                                    const client = clientsById[id];
                                    if (client) {
                                        return (
                                            <Link key={id} to={`/parties/${id}`}>
                                                {clientsById[id].name}
                                            </Link>
                                        );
                                    } else {
                                        return null;
                                    }
                                })}
                            </div>
                        );
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilterArrayOfIds(Object.values(clientsById)),
                size: 80
            },
            {
                header: "Personal number",
                accessorKey: "personalNumber"
            },
            {
                header: "Name",
                accessorKey: "name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/admin/users/${row.original._id}`}>{cellProps.getValue()}</Link>;
                },
                size: 70
            },
            {
                header: "Company",
                accessorKey: "company",
                filter: SelectColumnFilter
            },
            {
                header: "Frontend role",
                accessorKey: "frontendRoleId",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    const value = cellProps.getValue();
                    if (row.original.frontendRole) {
                        return <Link to={`/admin/roles/${value}`}>{rolesById[value].name}</Link>;
                    } else {
                        return null;
                    }
                }
            },
            {
                header: "Roles",
                accessorKey: "roleIds",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (!value || !Array.isArray(value)) {
                        return null;
                    }
                    const roleEntries = value
                        .map((id) => ({ id, role: rolesById[id] }))
                        .filter((entry): entry is { id: string; role: { _id: string; name: string } } => Boolean(entry.role));
                    if (roleEntries.length === 0) {
                        return null;
                    }
                    return (
                        <div className="min-w-0 flex flex-wrap items-baseline">
                            {roleEntries.map(({ id, role }, index) => (
                                <Fragment key={id}>
                                    {index > 0 ? ", " : null}
                                    <Link className="whitespace-nowrap" to={`/admin/roles/${id}`}>
                                        {role.name}
                                    </Link>
                                </Fragment>
                            ))}
                        </div>
                    );
                },
                filter: SelectColumnFilterArrayOfIds(Object.values(usedRolesById)),
                size: 100
            },
            {
                header: "Last login timestamp",
                accessorKey: "lastLoginTimestamp",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    const formatted = value ? value.substring(0, 16) : "";
                    return <div>{formatted}</div>;
                }
            },
            {
                header: "Last bankId login timestamp",
                accessorKey: "lastBankIdLoginTimestamp",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    const formatted = value ? value.substring(0, 16) : "";
                    return <div>{formatted}</div>;
                }
            },
            {
                header: "Last password update",
                accessorKey: "lastPasswordUpdate",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    const formatted = value ? value.substring(0, 16) : "";
                    return <div>{formatted}</div>;
                }
            },
            {
                header: "updateTimestamp",
                accessorKey: "updateTimestamp",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    const formatted = value ? value.substring(0, 16) : "";
                    return <div>{formatted}</div>;
                }
            },
            {
                header: "Updated by",
                accessorKey: "updateUserInfo",
                cell: (cellProps) => {
                    const updateUserInfo = cellProps.getValue() || "";
                    if (updateUserInfo) {
                        return <div>{updateUserInfo.name}</div>;
                    } else {
                        return "";
                    }
                }
            }
        ];
    }, [data]);

    if (loadingMe || loading) return <h3>loading..</h3>;
    if (errorMe) return <AdminUsersPageError title="Could not load your user profile" error={errorMe} />;
    if (error) return <AdminUsersPageError title="Could not load users" error={error} />;

    const meRoles = getMeData?.me?.roles;
    if (!meRoles || !Array.isArray(meRoles)) {
        return (
            <div className="m-4 p-4" role="alert" style={{ border: "1px solid #c00", backgroundColor: "#fff5f5", borderRadius: 4 }}>
                <p style={{ color: "#700" }}>Your user profile did not include role data. Try signing out and back in, or contact support.</p>
            </div>
        );
    }

    if (!userHaveAccessTo("Any", PermissionAssetEnum.Admin, meRoles)) {
        return <div />;
    }

    if (!data?.users || !Array.isArray(data.users) || !data?.roles || !Array.isArray(data.roles)) {
        return (
            <div className="m-4 p-4" role="alert" style={{ border: "1px solid #c00", backgroundColor: "#fff5f5", borderRadius: 4 }}>
                <p style={{ color: "#700" }}>The user list response was incomplete. Try refreshing the page.</p>
            </div>
        );
    }

    let { users } = data;
    users = users ? users : [];

    return (
        <div>
            <Fragment>
                <p>Number of users: {users.length}</p>
                <ReactTable columns={columns} data={users} defaultHiddenColumns={["personalNumber"]} />
            </Fragment>
        </div>
    );
}
